import { defineStore } from 'pinia'

export const useCookieConsentStore = defineStore('cookieConsent', {
    state: () => ({
        // États du consentement
        hasAnswered: false,
        consentGiven: false,
        consentDate: null,
        showBanner: false,

        // Types de cookies autorisés
        cookieTypes: {
            essential: true, // Toujours activés
            analytics: false,
            marketing: false,
            preferences: false
        },

        // Configuration
        bannerDismissed: false,
        consentVersion: '1.0' // Pour gérer les changements de politique
    }),

    getters: {
        // Vérifier si on doit afficher la bannière
        shouldShowBanner: (state) => {
            return !state.hasAnswered && !state.bannerDismissed
        },

        // Vérifier si les analytics sont autorisés
        canUseAnalytics: (state) => {
            return state.consentGiven && state.cookieTypes.analytics
        },

        // Vérifier si le marketing est autorisé
        canUseMarketing: (state) => {
            return state.consentGiven && state.cookieTypes.marketing
        },

        // Vérifier si les préférences sont autorisées
        canUsePreferences: (state) => {
            return state.consentGiven && state.cookieTypes.preferences
        },

        // Obtenir le statut général
        consentStatus: (state) => {
            if (!state.hasAnswered) return 'pending'
            if (state.consentGiven) return 'accepted'
            return 'declined'
        },

        // Vérifier si le consentement est expiré (1 an)
        isConsentExpired: (state) => {
            if (!state.consentDate) return false
            const expirationDate = new Date(state.consentDate)
            expirationDate.setFullYear(expirationDate.getFullYear() + 1)
            return new Date() > expirationDate
        }
    },

    actions: {
        // Initialiser le store depuis les cookies
        initializeFromCookies() {
            const consentCookie = useCookie('cookie_consent', {
                default: () => null,
                maxAge: 60 * 60 * 24 * 365 // 1 an
            })

            const consentData = consentCookie.value

            if (consentData) {
                try {
                    const data = typeof consentData === 'string'
                        ? JSON.parse(consentData)
                        : consentData

                    this.hasAnswered = true
                    this.consentGiven = data.consentGiven || false
                    this.consentDate = data.consentDate || null
                    this.cookieTypes = { ...this.cookieTypes, ...data.cookieTypes }
                    this.consentVersion = data.consentVersion || '1.0'

                    // Vérifier si le consentement est expiré
                    if (this.isConsentExpired) {
                        this.resetConsent()
                    }
                } catch (error) {
                    console.error('Erreur lors du parsing du cookie de consentement:', error)
                    this.resetConsent()
                }
            }

            // Vérifier si on doit afficher la bannière
            this.showBanner = this.shouldShowBanner

        },

        // Accepter tous les cookies
        acceptAllCookies() {
            this.consentGiven = true
            this.hasAnswered = true
            this.consentDate = new Date().toISOString()
            this.showBanner = false

            this.cookieTypes = {
                essential: true,
                analytics: true,
                marketing: true,
                preferences: true
            }

            this.saveToCookie()
            this.initializeAnalytics()
            this.emitConsentChange('accepted')
        },

        // Refuser les cookies non essentiels
        declineAllCookies() {
            this.consentGiven = false
            this.hasAnswered = true
            this.consentDate = new Date().toISOString()
            this.showBanner = false

            this.cookieTypes = {
                essential: true,
                analytics: false,
                marketing: false,
                preferences: false
            }

            this.saveToCookie()
            this.cleanupNonEssentialCookies()
            this.emitConsentChange('declined')
        },

        // Accepter avec des préférences personnalisées
        acceptWithPreferences(preferences) {
            this.consentGiven = true
            this.hasAnswered = true
            this.consentDate = new Date().toISOString()
            this.showBanner = false

            this.cookieTypes = {
                essential: true, // Toujours activés
                analytics: preferences.analytics || false,
                marketing: preferences.marketing || false,
                preferences: preferences.preferences || false
            }

            this.saveToCookie()

            if (this.cookieTypes.analytics) {
                this.initializeAnalytics()
            }

            this.emitConsentChange('customized')
        },

        // Sauvegarder dans le cookie
        saveToCookie() {
            const consentCookie = useCookie('cookie_consent', {
                maxAge: 60 * 60 * 24 * 365, // 1 an
                secure: true,
                sameSite: 'strict'
            })

            const dataToSave = {
                consentGiven: this.consentGiven,
                consentDate: this.consentDate,
                cookieTypes: this.cookieTypes,
                consentVersion: this.consentVersion
            }

            consentCookie.value = JSON.stringify(dataToSave)

        },

        // Réinitialiser le consentement
        resetConsent() {
            this.hasAnswered = false
            this.consentGiven = false
            this.consentDate = null
            this.showBanner = true
            this.bannerDismissed = false

            this.cookieTypes = {
                essential: true,
                analytics: false,
                marketing: false,
                preferences: false
            }

            if (process.client) {
                const consentCookie = useCookie('cookie_consent')
                consentCookie.value = null
            }

            this.cleanupNonEssentialCookies()
        },

        // Masquer temporairement la bannière (sans donner de consentement)
        dismissBanner() {
            this.showBanner = false
            this.bannerDismissed = true
        },

        // Nettoyer les cookies non essentiels
        cleanupNonEssentialCookies() {
            // Liste des cookies à supprimer si le consentement est refusé
            const cookiesToRemove = [
                '_ga', '_gid', '_gat', // Google Analytics
                'fbp', '_fbp', // Facebook Pixel
                '_hjid', '_hjFirstSeen', // Hotjar
                'intercom-*', // Intercom
                // Ajoutez d'autres cookies selon vos besoins
            ]

            cookiesToRemove.forEach(cookieName => {
                if (cookieName.includes('*')) {
                    // Gestion des wildcards
                    const prefix = cookieName.replace('*', '')
                    Object.keys(document.cookie).forEach(key => {
                        if (key.startsWith(prefix)) {
                            this.removeCookie(key)
                        }
                    })
                } else {
                    this.removeCookie(cookieName)
                }
            })

        },

        // Supprimer un cookie spécifique
        removeCookie(name) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`

        },

        // Initialiser les analytics si autorisés
        initializeAnalytics() {
            if (this.canUseAnalytics) {
                // Exemple avec Google Analytics
                if (window.gtag) {
                    window.gtag('consent', 'update', {
                        analytics_storage: 'granted'
                    })
                }

                // Autres services d'analytics...
            }
        },

        // Émettre un événement de changement de consentement
        emitConsentChange(type) {
            if (window.dispatchEvent) {
                const event = new CustomEvent('cookieConsentChanged', {
                    detail: {
                        type,
                        consentGiven: this.consentGiven,
                        cookieTypes: this.cookieTypes,
                        timestamp: new Date()
                    }
                })
                window.dispatchEvent(event)
            }
        },

        // Vérifier si un type de cookie spécifique est autorisé
        isCookieTypeAllowed(type) {
            if (type === 'essential') return true
            return this.consentGiven && this.cookieTypes[type]
        },

        // Obtenir les statistiques du consentement
        getConsentStats() {
            return {
                hasAnswered: this.hasAnswered,
                consentGiven: this.consentGiven,
                consentDate: this.consentDate,
                cookieTypes: this.cookieTypes,
                isExpired: this.isConsentExpired,
                version: this.consentVersion
            }
        }
    },

    persist: {
        key: 'cookie-consent-store',
        secure: process.env.NODE_ENV === 'production',
        paths: ['hasAnswered', 'bannerDismissed', 'consentVersion']
    }
})